// Reads `?focus=` and `?region=` from the URL — the contract used to send a
// pre-selected set of comuni (e.g. the top-10 of a recommended region) from
// /it/recommendations into the map.
//
// Format:
//   /it/map?focus=8042001,8042002,…&region=03
//
// `focus` — comma-separated 6-digit ISTAT codes (already canonical strings
// in the dataset, see ComuneLight.istat). Strict validation: anything that
// is not /^\d{4,6}$/ is dropped — a malformed share link cannot trip a
// runtime error.
//
// `region` — 2-digit ISO/ISTAT region code (01..20). Drives the banner copy
// (e.g. "10 recommended comuni · Lombardia") and is independent of focus —
// a user can have region without focus and vice versa.
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const ISTAT_RE = /^\d{4,6}$/;
const REGION_RE = /^\d{1,2}$/;
const MAX_FOCUS = 50;

export function useFocusedIstats(): Set<string> {
  const [search] = useSearchParams();
  const raw = search.get('focus') ?? '';
  return useMemo(() => {
    if (!raw) return new Set<string>();
    const codes = raw
      .split(',')
      .map((c) => c.trim())
      .filter((c) => ISTAT_RE.test(c))
      .slice(0, MAX_FOCUS);
    return new Set(codes);
  }, [raw]);
}

export function useFocusedRegion(): string | null {
  const [search] = useSearchParams();
  const raw = search.get('region') ?? '';
  if (!raw || !REGION_RE.test(raw)) return null;
  return raw.padStart(2, '0');
}
